import { ReactNode, useEffect, useState, useCallback } from "react";
import { inject, observer } from "mobx-react";
import classnames from "classnames";
import { isWindowDefined, resizeWatcher } from "@inferno/renderer-shared-core";
import type { Store } from "@inferno/renderer-shared-core";
import { COMPONENT_BREAKPOINTS } from "../ui/constants";
import styles from "./DynamicHeaderItem.module.scss";

export interface DynamicHeaderItemProps {
  store?: Store;
  className?: string;
  iconOnlyClassName?: string;
  buttonClassName?: string;
  ariaLabel: string;
  onClick?: () => void;
  isVisible?: boolean;
  breakpoints?: {
    showText?: number;
    iconOnly?: number;
  };
  content?: ReactNode;
  mobileContent?: ReactNode;
  iconOnlyContent?: ReactNode;
  isLoading?: boolean;
}

export const DynamicHeaderItem = inject("store")(
  observer(
    ({
      store,
      className,
      iconOnlyClassName,
      buttonClassName,
      ariaLabel,
      onClick,
      isVisible = true,
      breakpoints = {
        showText: COMPONENT_BREAKPOINTS.smallTabletBreakpoint,
        iconOnly: COMPONENT_BREAKPOINTS.headerGridBreakpoint,
      },
      content,
      mobileContent,
      iconOnlyContent,
      isLoading = false,
    }: DynamicHeaderItemProps) => {
      if (!store || !isVisible) {
        return null;
      }

      const [showText, setShowText] = useState<boolean | null>(null);
      const [hideTextOverride, setHideTextOverride] = useState<boolean | null>(null);
      const mounted = showText !== null || hideTextOverride !== null;

      const containerClass = classnames(styles.dynamicHeaderItem, className, {
        [styles.iconOnly]: hideTextOverride,
        [iconOnlyClassName || ""]: hideTextOverride && iconOnlyClassName,
      });

      const handleWindowResize = useCallback(() => {
        const width = window.innerWidth;
        const isPhone = width < COMPONENT_BREAKPOINTS.smallTabletBreakpoint; // < 768px is phone

        // On phones, always show icon only
        if (isPhone) {
          setShowText(false);
          setHideTextOverride(true);
        } else {
          // On tablet/desktop, use the existing responsive behavior
          if (breakpoints.showText) {
            if (width > breakpoints.showText) {
              setShowText(true);
            } else {
              setShowText(false);
            }
          }

          if (breakpoints.iconOnly) {
            if (width < breakpoints.iconOnly) {
              setHideTextOverride(true);
            } else {
              setHideTextOverride(false);
            }
          }
        }
      }, [breakpoints.showText, breakpoints.iconOnly]);

      useEffect(() => {
        if (isWindowDefined()) {
          handleWindowResize();
          resizeWatcher.onWidthChange.subscribe(handleWindowResize);
          return () => resizeWatcher.onWidthChange.unsubscribe(handleWindowResize);
        }
      }, [handleWindowResize]);

      if (!mounted) {
        return null;
      }

      // Determine which content to show based on responsive state
      let displayContent: ReactNode;
      if (hideTextOverride) {
        displayContent = iconOnlyContent;
      } else if (showText) {
        displayContent = content;
      } else {
        // For non-mobile screens that don't show full text, use mobileContent if available
        displayContent = mobileContent || content;
      }

      return (
        <div className={containerClass}>
          <button
            onClick={onClick}
            className={classnames(styles.toggleBtn, buttonClassName)}
            aria-label={ariaLabel}
            disabled={isLoading}
          >
            <span className={styles.btnContent}>{displayContent}</span>
          </button>
        </div>
      );
    },
  ),
);
